import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Outlet, useLocation } from "react-router-dom";
import { Head } from "vite-react-ssg";
import { api } from "../lib/api";

type Page = {
  title: string; seo_title: string; meta_description: string; keywords: string;
  canonical_url: string; is_indexable: boolean; is_followable: boolean;
  schema_type: string; custom_html: string; content_html: string;
};

const UNSEARCHABLE_PATHS = new Set(["/404", "/products/wordpress", "/free-server"]);

function appendHeadHTML(html: string) {
  const template = document.createElement("template");
  template.innerHTML = html;
  const added: Node[] = [];
  for (const source of Array.from(template.content.children)) {
    let node: HTMLElement;
    if (source.tagName === "SCRIPT") {
      node = document.createElement("script");
      for (const attribute of Array.from(source.attributes)) node.setAttribute(attribute.name, attribute.value);
      node.textContent = source.textContent;
    } else {
      node = source.cloneNode(true) as HTMLElement;
    }
    node.dataset.gerdooCmsHead = "true";
    document.head.appendChild(node);
    added.push(node);
  }
  return () => added.forEach((node) => node.parentNode?.removeChild(node));
}

export function ManagedLandingPage() {
  const { pathname } = useLocation();
  const [page, setPage] = useState<Page | null>(null);
  const [beforeFooterHost, setBeforeFooterHost] = useState<HTMLElement | null>(null);

  useEffect(() => {
    let active = true;
    setPage(null);
    api.get("/landing-page", { params: { path: pathname } })
      .then((response) => { if (active) setPage(response.data); })
      .catch(() => {});
    return () => { active = false; };
  }, [pathname]);

  useEffect(() => {
    const footer = document.querySelector("footer");
    if (!footer?.parentNode) return;
    const host = document.createElement("div");
    host.className = "cms-landing-bottom-content";
    footer.parentNode.insertBefore(host, footer);
    setBeforeFooterHost(host);
    return () => { setBeforeFooterHost(null); host.remove(); };
  }, [pathname, page?.content_html]);

  useEffect(() => {
    if (!page?.custom_html) return;
    return appendHeadHTML(page.custom_html);
  }, [page?.custom_html]);

  const robots = page ? `${page.is_indexable ? "index" : "noindex"}, ${page.is_followable ? "follow" : "nofollow"}` : "index, follow";
  const canonical = page?.canonical_url || `https://gerdoo.cloud${pathname}`;
  const schema = page ? { "@context": "https://schema.org", "@type": page.schema_type || "WebPage", name: page.title || page.seo_title, url: canonical, description: page.meta_description } : null;

  // Pagefind indexes only pages whose content sits under data-pagefind-body
  // (see the landing build script), so the 404 and pure-redirect stubs opt out.
  const searchable = !UNSEARCHABLE_PATHS.has(pathname);

  return <>
    {searchable ? <div data-pagefind-body className="contents"><Outlet /></div> : <Outlet />}
    {page && <Head>
      {(page.seo_title || page.title) && <title>{page.seo_title || page.title}</title>}
      {page.meta_description && <meta name="description" content={page.meta_description} />}
      {page.keywords && <meta name="keywords" content={page.keywords} />}
      <meta name="robots" content={robots} /><link rel="canonical" href={canonical} />
      {schema && <script type="application/ld+json">{JSON.stringify(schema)}</script>}
    </Head>}
    {beforeFooterHost && page?.content_html && createPortal(
      <div dangerouslySetInnerHTML={{ __html: page.content_html }} />,
      beforeFooterHost,
    )}
  </>;
}
